import { getUrlParam } from "./urlUtils";
import { format } from "date-fns";

// Helper function to check if internal mode is enabled
const getIsInternalMode = (): boolean => {
  return getUrlParam("internal") === "true";
};

export interface ClosedTime {
  closed_date: string;
  time_range: string | null;
}

// opening_hours is free-form JSON from the clinics table: a day maps to a range
// object or an array of them. One alias, one disable, instead of one disable per
// cast site.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type OpeningHours = Record<string, any>;

export const filterSlotsByClosedTimes = (
  slots: string[],
  date: Date,
  clinic_closed_times?: ClosedTime[],
): string[] => {
  if (!clinic_closed_times || clinic_closed_times.length === 0) {
    return slots;
  }

  const dateString = format(date, "yyyy-MM-dd");

  // Find closed times for this specific date
  const closedTimesForDate = clinic_closed_times.filter(
    (ct) => ct.closed_date === dateString,
  );

  if (closedTimesForDate.length === 0) {
    return slots;
  }

  // Check if ANY closed time entry has no time_range (meaning entire day is closed)
  const hasFullDayClosures = closedTimesForDate.some((ct) => !ct.time_range);
  if (hasFullDayClosures) {
    // Entire day is closed
    return [];
  }

  // If there are time ranges specified, only keep slots within those ranges
  const availableTimeRanges = closedTimesForDate
    .filter((ct) => ct.time_range)
    .map((ct) => {
      const [startTime, endTime] = ct.time_range!.split("-");
      const [startHour, startMinute] = startTime.split(":").map(Number);
      const [endHour, endMinute] = endTime.split(":").map(Number);

      return {
        startMinutes: startHour * 60 + startMinute,
        endMinutes: endHour * 60 + endMinute,
      };
    });

  // If no time ranges are specified for this date, day is already handled in isDateDisabled
  if (availableTimeRanges.length === 0) {
    return slots;
  }

  // Keep only slots that fall within available time ranges
  return slots.filter((slot) => {
    const [slotHour, slotMinute] = slot.split(":").map(Number);
    const slotTimeInMinutes = slotHour * 60 + slotMinute;

    // Check if this slot falls within any available time range
    return availableTimeRanges.some(
      (range) =>
        slotTimeInMinutes >= range.startMinutes &&
        slotTimeInMinutes < range.endMinutes,
    );
  });
};

export const generateTimeSlots = (
  date: Date,
  isWeekend: boolean,
  clinicOpeningHours?: OpeningHours,
  isInternal: boolean = false,
  clinic_closed_times?: ClosedTime[],
) => {
  const slots: string[] = [];

  const day = date.getDay(); // 0 is Sunday, 1 is Monday, etc.
  const dayNames = [
    "sunday",
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
  ];
  const dayName = dayNames[day].toLowerCase();

  // For weekends, only generate slots if clinic has opening hours for that day
  if (isWeekend && !(clinicOpeningHours && clinicOpeningHours[dayName])) {
    return slots;
  }

  // A clinic that publishes opening hours is closed on any day they leave out.
  // The default hours below are only for clinics with no opening hours at all;
  // isDateDisabled already reads a missing day as closed.
  if (clinicOpeningHours && !clinicOpeningHours[dayName]) {
    return slots;
  }

  // Check if we have specific opening hours for this clinic and day
  if (clinicOpeningHours && clinicOpeningHours[dayName]) {
    try {
      const dayHours = clinicOpeningHours[dayName];

      // If the clinic is closed this day, return empty array
      if (!dayHours || dayHours.closed) {
        return slots;
      }

      // Handle both formats: array (with lunch breaks) and object (single time slot)
      const timeRanges = Array.isArray(dayHours) ? dayHours : [dayHours];

      // Get the time slot gap (default 30 minutes) from the parent clinicOpeningHours object
      const slotGap = clinicOpeningHours.gap || 30;

      // Process each time range
      for (const range of timeRanges) {
        // Get opening and closing hours, handling half-hour precision
        const openTimeString = range.open || "09:00";
        const closeTimeString = range.close || "18:00";

        // Parse opening time
        const [openHour, openMinute] = openTimeString
          .split(":")
          .map((num) => parseInt(num, 10));
        const openTimeInMinutes = openHour * 60 + (openMinute || 0);

        // Parse closing time
        const [closeHour, closeMinute] = closeTimeString
          .split(":")
          .map((num) => parseInt(num, 10));
        const closeTimeInMinutes = closeHour * 60 + (closeMinute || 0);

        // Generate slots in specified minute increments
        for (
          let timeInMinutes = openTimeInMinutes;
          timeInMinutes < closeTimeInMinutes;
          timeInMinutes += slotGap
        ) {
          const hour = Math.floor(timeInMinutes / 60);
          const minute = timeInMinutes % 60;

          const formattedHour = hour.toString().padStart(2, "0");
          const formattedMinute = minute.toString().padStart(2, "0");
          const timeString = `${formattedHour}:${formattedMinute}`;

          if (isTimeSlotAvailable(hour, minute, date, isInternal)) {
            slots.push(timeString);
          }
        }
      }

      // Filter out slots within blacklist time ranges
      return filterSlotsByClosedTimes(slots, date, clinic_closed_times);
    } catch (error) {
      console.error("Error processing clinic opening hours:", error);
    }
  }

  // Fallback to default logic if no specific clinic hours are available
  const isTuesdayOrThursday = day === 2 || day === 4; // Tuesday is 2, Thursday is 4

  // Generate all available time slots for the day
  for (let hour = 9; hour <= 18; hour++) {
    // Skip morning hours before 13:00 on Tuesdays and Thursdays
    if (isTuesdayOrThursday && hour < 13) {
      continue;
    }

    // Add slots at the hour (XX:00)
    if (isTimeSlotAvailable(hour, 0, date, isInternal)) {
      const timeString = `${hour.toString().padStart(2, "0")}:00`;
      slots.push(timeString);
    }

    // Add slots at half past the hour (XX:30)
    if (isTimeSlotAvailable(hour, 30, date, isInternal)) {
      const timeString = `${hour.toString().padStart(2, "0")}:30`;
      slots.push(timeString);
    }
  }

  // Filter out slots within blacklist time ranges
  return filterSlotsByClosedTimes(slots, date, clinic_closed_times);
};

export const isTimeSlotAvailable = (
  hour: number,
  minute: number,
  date: Date,
  isInternal: boolean = false,
): boolean => {
  try {
    const now = new Date();
    const slotTime = new Date(date);
    slotTime.setHours(hour, minute, 0, 0);

    // Auto-detect internal mode if not explicitly provided
    const internalMode = isInternal || getIsInternalMode();

    // For internal bookings, allow any future time slot
    if (internalMode) {
      return slotTime >= now;
    }

    // Time slot must be at least 4 hours in the future
    const minimumTime = new Date(now.getTime() + 4 * 60 * 60 * 1000);

    return slotTime > minimumTime;
  } catch (error) {
    console.error("Error checking time slot availability:", error);
    return false;
  }
};
